import { Line } from '@react-three/drei'
import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { AssetVizMode } from 'features/assetViz/index'
import { useSurfaceProps } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import { useSurfaceLines } from 'features/assetViz/objects/surfaces/util/useSurfaceLines'
import {
  overlayMaterialDefines,
  RenderLayerID,
  RenderOrder,
} from 'features/assetViz/utils/renderingDefines'
import React from 'react'
import { Color } from 'three'
import {
  GeckoCoatingRegion,
  GeckoComponentPath,
  GeckoFeature,
  GeckoLinerRegion,
} from 'types'

export enum SurfaceLineSelectionType {
  FeatureLine,
  SelectableFeatureLine,
  LineForFeatureSelection,
}

/**
 * Feature that draws lines on any mesh given a path.
 * @param path Gecko Component Path to draw
 * @param featureModel feature
 * @param lineSelectionType Optional. Default is FeatureLine
 *                        * FeatureLine: line for feature but not selectable
 *                        * SelectableFeatureLine: line for feature but not selectable
 *                        * LineForFeatureSelection: line not for feature, solely for selection
 * @returns
 */

export const SurfaceLineFeature3D: React.FC<{
  path: GeckoComponentPath
  featureModel: GeckoFeature
  lineSelectionType?: SurfaceLineSelectionType
}> = ({
  path,
  featureModel,
  lineSelectionType = SurfaceLineSelectionType.FeatureLine,
}) => {
  const { componentModel } = useSurfaceProps()
  const {
    showFeatures,
    weldColorString,
    selection,
    draftSelection,
    mode,
    clipConfig,
    corrosionRegionColorString,
  } = useAssetVizInternal()

  const showThisFeature = React.useMemo(
    () =>
      typeof showFeatures === 'boolean'
        ? showFeatures
        : showFeatures({ feature: featureModel, componentModel }),
    [featureModel, componentModel, showFeatures]
  )

  const lines = useSurfaceLines(
    [path],
    lineSelectionType !== SurfaceLineSelectionType.LineForFeatureSelection
  )

  const { lineWidth, lineColor } = React.useMemo(
    () => ({
      lineWidth:
        selection === featureModel || draftSelection === featureModel ? 5 : 3,
      lineColor: new Color(
        featureModel instanceof GeckoCoatingRegion ||
        featureModel instanceof GeckoLinerRegion
          ? corrosionRegionColorString
          : (selection === featureModel || draftSelection === featureModel) &&
              lineSelectionType !== SurfaceLineSelectionType.FeatureLine
            ? 'white'
            : weldColorString
      ).convertSRGBToLinear(),
    }),
    [
      draftSelection,
      featureModel,
      selection,
      weldColorString,
      lineSelectionType,
      corrosionRegionColorString,
    ]
  )

  const renderLayer = React.useMemo(
    () =>
      !showThisFeature
        ? RenderLayerID.InvisibleNonCollidable
        : mode === AssetVizMode.AllFeaturesSelectable &&
            lineSelectionType === SurfaceLineSelectionType.SelectableFeatureLine
          ? RenderLayerID.VisibleCollidable
          : lineSelectionType ===
                SurfaceLineSelectionType.LineForFeatureSelection &&
              !(selection === featureModel || draftSelection === featureModel)
            ? RenderLayerID.InvisibleNonCollidable
            : RenderLayerID.VisibleNonCollidable,
    [
      showThisFeature,
      mode,
      lineSelectionType,
      selection,
      draftSelection,
      featureModel,
    ]
  )

  const renderOrder = React.useMemo(
    () =>
      selection === featureModel || draftSelection === featureModel
        ? RenderOrder.SelectedFeature
        : RenderOrder.Markup,
    [selection, featureModel, draftSelection]
  )

  return lines === undefined || lines.xyzs.length === 0 ? null : (
    <>
      <Line
        layers={renderLayer}
        segments
        renderOrder={renderOrder}
        points={lines.xyzs}
        linewidth={lineWidth}
        dashed={false}
        userData={{
          extrudingFeature:
            lineSelectionType === SurfaceLineSelectionType.SelectableFeatureLine
              ? featureModel
              : undefined,
        }}
        color={lineColor}
        clippingPlanes={clipConfig.planes}
        {...overlayMaterialDefines}
      />
    </>
  )
}
